import { useEffect, useState } from "react";
import { isoWeekMonday, isoWeekOf, weekRangeLabel } from "@/lib/days";
import type { WeekUpdateCandidate, WidgetChange } from "@/lib/week-updates";
import { Modal } from "./Modal";

interface Props {
  open: boolean;
  candidates: WeekUpdateCandidate[];
  onClose: () => void;
  onApply: (weekIds: string[]) => Promise<void>;
}

/**
 * Where a candidate sits relative to now. Only ever "denne uken" or a future
 * week: a past week is frozen and never reaches this list.
 */
function relationLabel(startDate: string, today: Date): string {
  const { year, weekNumber } = isoWeekOf(today);
  const thisMonday = isoWeekMonday(year, weekNumber);
  if (startDate <= thisMonday) return "denne uken";
  const weeksAhead = Math.round(
    (Date.parse(startDate) - Date.parse(thisMonday)) / (7 * 86400000),
  );
  return weeksAhead === 1 ? "neste uke" : `om ${weeksAhead} uker`;
}

function changeLabel(change: WidgetChange): string {
  if (change.change === "added") return `+ ${change.label}`;
  if (change.change === "changed") return `~ ${change.label}`;
  return change.dataDescription
    ? `− ${change.label} slettes (${change.dataDescription})`
    : `− ${change.label} slettes`;
}

export function UpdateWeeksModal({
  open,
  candidates,
  onClose,
  onApply,
}: Props) {
  // Pre-checked: the common path is "yes, all of them", and the row spells
  // out anything destructive before it is confirmed.
  const [selected, setSelected] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) setSelected(candidates.map((c) => c.id));
  }, [open, candidates]);

  if (!open) return null;

  const today = new Date();
  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const apply = async () => {
    if (applying) return;
    setApplying(true);
    try {
      await onApply(selected);
      onClose();
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} data-testid="update-weeks-modal">
      <h2>Oppdater uker?</h2>
      <p className="modal-body">
        Uken blir gjort lik ukesmalen — ikke bare de siste endringene.
      </p>

      <ul className="update-weeks-list">
        {candidates.map((candidate) => {
          const checked = selected.includes(candidate.id);
          return (
            <li key={candidate.id} className="update-weeks-item">
              <label className="update-weeks-row">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(candidate.id)}
                  data-testid={`update-week-checkbox-${candidate.id}`}
                />
                <span className="update-weeks-title">
                  Uke {candidate.data.weekNumber} ·{" "}
                  {weekRangeLabel(candidate.data.startDate)}
                </span>
                <span className="update-weeks-when">
                  {relationLabel(candidate.data.startDate, today)}
                </span>
              </label>
              <ul
                className="update-weeks-changes"
                data-testid={`update-week-changes-${candidate.id}`}
              >
                {candidate.changes.map((change) => (
                  <li
                    key={`${change.change}-${change.widgetId}`}
                    className={`update-weeks-change update-weeks-change--${change.change}`}
                  >
                    {changeLabel(change)}
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>

      <p className="update-weeks-note" data-testid="update-weeks-future-note">
        Uker du oppretter senere bruker alltid denne malen.
      </p>

      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="update-weeks-cancel"
        >
          Ikke oppdater
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={apply}
          disabled={selected.length === 0 || applying}
          data-testid="update-weeks-confirm"
        >
          Oppdater uker
        </button>
      </div>
    </Modal>
  );
}
